import React, { useState, useRef, useEffect } from "react";
import { AdminPermission } from "../../api/generated/models/AdminPermission";
import { ADMIN_NAV, SUPER_ADMIN_LABEL } from "../../config/adminNav";
import {
  GroupCheckState,
  PermissionOption,
  collectPermissionGroups,
  grantBlockReason,
  groupCheckState,
  toggleGroup,
  visiblePermissionGroups,
} from "../../helpers/adminPermissions";

interface PermissionsCheckboxGroupProps {
  selectedPermissions: AdminPermission[];
  onChange: (permissions: AdminPermission[]) => void;
  // The logged-in admin's own permissions: what they may grant is checked
  // against these, mirroring the backend.
  grantorPermissions: AdminPermission[];
  disabled?: boolean;
  // Why the whole form is read-only (e.g. editing a Super Admin), shown on top.
  lockedReason?: string | null;
}

// Laid out like the sidebar: one group per sidebar group, labelled as in adminNav.
const PERMISSION_GROUPS = collectPermissionGroups(ADMIN_NAV);

const SUPER_ADMIN_OPTION: PermissionOption = {
  permission: AdminPermission.SUPER_ADMIN,
  label: SUPER_ADMIN_LABEL,
};

const permissionDescriptions: Record<AdminPermission, string> = {
  [AdminPermission.SUPER_ADMIN]:
    "Full access to all features and permissions. Super Admins have complete control over the platform and can manage all aspects of the system. Super Admins can only be created, updated, or removed by support and not through the dashboard.",
  [AdminPermission.ADMIN_MANAGEMENT]:
    "Provides access to the Admins page: invite admins, resend invitations, change their permissions and remove them. Only Super Admins can grant it, and only Super Admins can edit an admin who has it.",
  [AdminPermission.TRANSACTIONS]:
    "Provides access to the All transactions page, where you can view transactions across all merchants, including the Visa history of card transactions.",
  [AdminPermission.FAILED_TRANSACTIONS]:
    "Provides access to the Failed transactions page, listing transactions that could not be processed together with their error details.",
  [AdminPermission.OFFLINE_TRANSACTIONS]:
    "Provides access to the Offline transactions page, where you can manage offline campaigns, upload their transaction reports and view their transactions.",
  [AdminPermission.USERS]:
    "Provides access to the Users page, where you can search app users and view their details. Deleting users is reserved for Super Admins.",
  [AdminPermission.CASHBACK_REQUESTS]:
    "Provides access to the Cashback requests page, where you can review, accept and decline users' cashback requests and file claims with the affiliate networks.",
  [AdminPermission.TOP_CUSTOMERS]:
    "Provides access to the Points brackets page, showing how many customers hold how many points.",
  [AdminPermission.ERASURES]:
    "Provides access to the Erasures page, which tracks the deletion of user accounts and their data, step by step.",
  [AdminPermission.PARTNER_MANAGEMENT]:
    "Provides access to the Partners pages: partner applications and onboarding, partner details, partner dashboard accounts and invitations, and logging in as a partner.",
  [AdminPermission.FEATURED_PARTNERS]:
    "Provides access to the Featured page, where you can manage and curate the groups of featured partners displayed in the app.",
  [AdminPermission.TOP_MERCHANTS]:
    "Provides access to the Top merchants page, where you can view analytics and performance metrics for top merchants.",
  [AdminPermission.PARTNER_NETWORK_REPORT]:
    "Provides access to the Network report page, where you can view merchants grouped by affiliate network (PartnerAds, Adtraction, Daisycon, TradeDoubler, CJ, Awin, Impact) with their start/end dates, profile views, and transaction counts.",
  [AdminPermission.VISA]:
    "Provides access to the Visa page, where you can view the Visa transaction history and look up business external IDs.",
  [AdminPermission.PROMO_CODES]:
    "Provides access to the Promo codes page, listing the imported promo code pools and the codes users have claimed.",
  [AdminPermission.OPT_IN_OFFERS]:
    "Provides access to the Opt-in Offers page, listing who opted in to opt-in partners and who missed cashback by paying without opting in, with CSV export.",
  [AdminPermission.ANALYTICS]:
    "Provides access to the Activity section: user activity (Visitors and Users), the platform point balance, and the Search and Clicks customer-journey funnels. Also shows the App activity figures on the dashboard home.",
  [AdminPermission.SEARCH_SUGGESTIONS]:
    "Provides access to the Zero result suggestion page, where you can see searches that returned nothing and add, edit and delete the suggestions shown for them.",
  [AdminPermission.APP_PERFORMANCE]:
    "Provides access to the App performance page, where you can view mobile app metrics and usage statistics.",
  [AdminPermission.KPIREPORT]:
    "Provides access to the KPI report page, where you can view comprehensive business performance reports. Also shows the Platform figures on the dashboard home.",
  [AdminPermission.REVENUE_SHARING_REPORT]:
    "Provides access to the Revenue sharing page showing RC revenue (total cashback minus user cashback) per month and quarter, broken down by Cardlinking, Affiliate networks, API partners, Reporting partners, and Webshop.",
  [AdminPermission.INSIGHTS]:
    "Provides access to the Campaigns page, showing newsletter and push notification campaign statistics.",
  [AdminPermission.NOTIFICATIONS]:
    "Provides access to the Send push page, where you can send custom and segmented push notifications and view their delivery history.",
  [AdminPermission.POPUP_CAMPAIGNS]:
    "Provides access to Pop-up campaigns, where you can create, target, publish and reorder the in-app pop-ups shown when users open the app.",
  [AdminPermission.BILLING]:
    "Provides access to the Invoicing page, where you can start bill runs and view invoices and partners' unsettled amounts.",
  [AdminPermission.WEBSHOP]:
    "Provides access to the Webshop page, where you can view products and purchase history and retry or resend gift cards.",
};

const SelectAllCheckbox: React.FC<{
  groupLabel: string;
  state: GroupCheckState;
  disabled: boolean;
  onToggle: () => void;
}> = ({ groupLabel, state, disabled, onToggle }) => {
  const ref = useRef<HTMLInputElement>(null);

  // `indeterminate` exists only as a DOM property, not as an attribute.
  useEffect(() => {
    if (ref.current) ref.current.indeterminate = state === "indeterminate";
  }, [state]);

  return (
    <label
      className={`flex items-center gap-2 text-xs text-gray-600 ${
        disabled ? "opacity-50 cursor-not-allowed" : "cursor-pointer"
      }`}
    >
      <input
        ref={ref}
        type="checkbox"
        checked={state === "checked"}
        onChange={onToggle}
        disabled={disabled}
        aria-label={`Select all ${groupLabel}`}
        className="w-4 h-4 text-purple-600 border-gray-300 rounded focus:ring-purple-500"
      />
      Select all
    </label>
  );
};

const PermissionsCheckboxGroup: React.FC<PermissionsCheckboxGroupProps> = ({
  selectedPermissions,
  onChange,
  grantorPermissions,
  disabled = false,
  lockedReason = null,
}) => {
  const [openPopover, setOpenPopover] = useState<AdminPermission | null>(null);
  const popoverRef = useRef<HTMLDivElement>(null);

  // Close popover when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        popoverRef.current &&
        !popoverRef.current.contains(event.target as Node)
      ) {
        setOpenPopover(null);
      }
    };

    if (openPopover) {
      document.addEventListener("mousedown", handleClickOutside);
      return () => {
        document.removeEventListener("mousedown", handleClickOutside);
      };
    }
  }, [openPopover]);

  const isReadOnly = disabled || Boolean(lockedReason);
  const groups = visiblePermissionGroups(PERMISSION_GROUPS, grantorPermissions);

  // Whether the admin may tick this permission. Super Admin never qualifies.
  const canSelect = (permission: AdminPermission) =>
    !isReadOnly && grantBlockReason(permission, grantorPermissions) === null;

  // A permission the admin cannot grant stays untickable, but if the edited
  // admin already holds it, it can still be removed: the backend refuses to
  // save it either way.
  const isOptionDisabled = (permission: AdminPermission) =>
    isReadOnly ||
    permission === AdminPermission.SUPER_ADMIN ||
    (!canSelect(permission) && !selectedPermissions.includes(permission));

  const handleToggle = (permission: AdminPermission) => {
    if (isOptionDisabled(permission)) return;

    const isSelected = selectedPermissions.includes(permission);
    if (isSelected) {
      onChange(selectedPermissions.filter((p) => p !== permission));
    } else {
      onChange([...selectedPermissions, permission]);
    }
  };

  const togglePopover = (
    e: React.MouseEvent,
    permission: AdminPermission
  ) => {
    e.preventDefault();
    e.stopPropagation();
    setOpenPopover(openPopover === permission ? null : permission);
  };

  const renderPermissionCheckbox = ({ permission, label }: PermissionOption) => {
    const isSelected = selectedPermissions.includes(permission);
    const isDisabled = isOptionDisabled(permission);
    // Super Admin has its own note below its box; a locked form explains itself on top.
    const reason =
      permission === AdminPermission.SUPER_ADMIN || lockedReason
        ? null
        : grantBlockReason(permission, grantorPermissions);
    const isPopoverOpen = openPopover === permission;

    return (
      <div key={permission} className="relative">
        <label
          className={`flex items-center gap-2 p-3 border rounded-lg cursor-pointer transition-colors ${
            isSelected
              ? "bg-purple-50 border-purple-300"
              : "bg-white border-gray-300 hover:bg-gray-50"
          } ${isDisabled ? "opacity-50 cursor-not-allowed" : ""}`}
        >
          <input
            type="checkbox"
            checked={isSelected}
            onChange={() => handleToggle(permission)}
            disabled={isDisabled}
            className="w-4 h-4 text-purple-600 border-gray-300 rounded focus:ring-purple-500 flex-shrink-0"
          />
          <span className="flex-1 min-w-0">
            <span className="block text-sm">{label}</span>
            {reason && (
              <span className="block text-xs text-gray-500">{reason}</span>
            )}
          </span>
          <button
            type="button"
            onClick={(e) => togglePopover(e, permission)}
            className="flex-shrink-0 w-5 h-5 rounded-full border border-gray-400 text-gray-600 hover:bg-gray-100 hover:border-gray-500 flex items-center justify-center text-xs font-medium transition-colors"
            aria-label={`Info about ${label}`}
          >
            ?
          </button>
        </label>

        {/* Popover */}
        {isPopoverOpen && (
          <div
            ref={popoverRef}
            className="absolute z-50 mt-2 w-72 bg-white border border-gray-300 rounded-lg shadow-lg p-4"
            style={{ top: "100%", right: 0 }}
          >
            <div className="flex items-start justify-between mb-2">
              <h4 className="font-medium text-sm text-gray-900">{label}</h4>
              <button
                type="button"
                onClick={() => setOpenPopover(null)}
                className="text-gray-400 hover:text-gray-600 -mt-1 -mr-1"
                aria-label="Close"
              >
                <svg
                  className="w-4 h-4"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M6 18L18 6M6 6l12 12"
                  />
                </svg>
              </button>
            </div>
            <p className="text-xs text-gray-600 leading-relaxed">
              {permissionDescriptions[permission]}
            </p>
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="space-y-6">
      {lockedReason && (
        <div className="bg-orange-50 border border-orange-200 text-orange-800 px-4 py-3 rounded-lg text-sm">
          <strong>Note:</strong> {lockedReason}
        </div>
      )}

      {/* Super Admin implies every permission and is never granted from here. */}
      <div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          {renderPermissionCheckbox(SUPER_ADMIN_OPTION)}
        </div>
        <p className="mt-2 text-xs text-gray-500">
          Super Admins can only be created by support and not through the dashboard
        </p>
      </div>

      {groups.map((group) => {
        const headingId = `permission-group-${group.id}`;
        const selectable = group.options.some((option) =>
          canSelect(option.permission)
        );
        return (
          <div key={group.id} role="group" aria-labelledby={headingId}>
            <div className="flex items-center justify-between mb-2">
              <span
                id={headingId}
                className="block text-sm font-medium text-gray-700"
              >
                {group.label}
              </span>
              <SelectAllCheckbox
                groupLabel={group.label}
                state={groupCheckState(group, selectedPermissions, canSelect)}
                disabled={!selectable}
                onToggle={() =>
                  onChange(toggleGroup(group, selectedPermissions, canSelect))
                }
              />
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              {group.options.map(renderPermissionCheckbox)}
            </div>
          </div>
        );
      })}
    </div>
  );
};

export default PermissionsCheckboxGroup;
